/*
  Course finder.

  Suggests courses, not products. Typing "aug" offers Augusta once, even
  though two Augusta artworks exist, because the visitor is picking a
  course. Choosing one loads the tag filtered collection URL, so the grid
  below narrows to every print of that course and the result is a real,
  shareable address rather than a transient client side state.

  The course list is embedded in the page by the section, so matching is
  instant and needs no request per keystroke. It also sidesteps
  products_per_page, which caps at 36 while the collection holds 38: the
  suggestions come from the collection's tags, not from what the page
  happens to have rendered.

  Progressive enhancement: the field lives in a form posting to /search,
  so with JavaScript off, or if anything here fails, Enter still runs a
  normal search.
*/

const NORMALISE = (value) =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9 ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const LIMIT = 7;

class IronwoodCourseFinder extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-finder-input]');
    this.results = this.querySelector('[data-finder-results]');
    this.clearButton = this.querySelector('[data-finder-clear]');
    this.status = this.querySelector('[data-finder-status]');
    const data = this.querySelector('[data-finder-courses]');
    if (!this.input || !this.results || !data) return;

    this.collectionUrl = this.dataset.collectionUrl || '';

    try {
      this.entries = JSON.parse(data.textContent).map((c) => ({
        ...c,
        key: NORMALISE(c.name),
      }));
    } catch {
      return; // leave the plain form working
    }

    this.matches = [];
    this.active = -1;

    this.onInput = this.onInput.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onFocus = this.onFocus.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.clear = this.clear.bind(this);

    this.input.addEventListener('input', this.onInput);
    this.input.addEventListener('keydown', this.onKeydown);
    this.input.addEventListener('focus', this.onFocus);
    this.addEventListener('focusout', this.onBlur);
    this.clearButton?.addEventListener('click', this.clear);
  }

  disconnectedCallback() {
    this.input?.removeEventListener('input', this.onInput);
    this.input?.removeEventListener('keydown', this.onKeydown);
    this.input?.removeEventListener('focus', this.onFocus);
    this.removeEventListener('focusout', this.onBlur);
    this.clearButton?.removeEventListener('click', this.clear);
  }

  find(query) {
    const terms = NORMALISE(query).split(' ').filter(Boolean);
    if (!terms.length) return [];
    const whole = terms.join(' ');

    // Ranked so a course name always beats a place name. Typing "Augusta"
    // must offer the Augusta course, not every course in Georgia, even
    // though both are legitimate matches.
    const RANK = { course: 0, state: 1, country: 2 };
    const scored = [];

    for (const entry of this.entries) {
      let score;
      if (entry.key === whole) score = 0;
      else if (entry.key.startsWith(whole)) score = 1;
      else if (terms.every((t) => entry.key.includes(t))) score = 2;
      else continue;
      scored.push({ entry, score: score * 10 + RANK[entry.kind] });
    }

    scored.sort((a, b) => a.score - b.score || a.entry.name.localeCompare(b.entry.name));
    return scored.slice(0, LIMIT).map((hit) => hit.entry);
  }

  onFocus() {
    if (this.input.value.trim()) this.onInput();
  }

  onInput() {
    const query = this.input.value.trim();
    if (this.clearButton) this.clearButton.hidden = query === '';

    if (query.length < 1) {
      this.close();
      return;
    }
    this.render(query, this.find(query));
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      this.close();
      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (!this.matches.length) return;
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      this.setActive((this.active + step + this.matches.length) % this.matches.length);
      return;
    }

    if (event.key === 'Enter') {
      // A highlighted suggestion wins. Otherwise a single match is
      // unambiguous enough to follow. Failing both, the form submits to
      // Shopify search, which is the sensible fallback.
      const choice =
        this.active >= 0 ? this.matches[this.active] : this.matches.length === 1 ? this.matches[0] : null;
      if (choice) {
        event.preventDefault();
        window.location.href = choice.url;
      }
    }
  }

  onBlur(event) {
    if (!this.contains(event.relatedTarget)) this.close();
  }

  render(query, matches) {
    this.matches = matches;
    this.active = -1;

    if (!matches.length) {
      this.results.innerHTML = `<li class="iw-finder__empty">Nothing matches "${query}".</li>`;
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.say(`Nothing matches ${query}`);
      return;
    }

    const KIND_LABEL = { course: 'Course', state: 'State', country: 'Country' };

    this.results.innerHTML = matches
      .map(
        (m, i) => `<li role="option" id="iw-finder-option-${i}" aria-selected="false">
            <a class="iw-finder__result" href="${m.url}" tabindex="-1">
              <span class="iw-finder__result-name">${m.name}</span>
              <span class="iw-finder__result-kind">${KIND_LABEL[m.kind] || ''}</span>
            </a>
          </li>`
      )
      .join('');

    this.results.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
    this.say(`${matches.length} ${matches.length === 1 ? 'suggestion' : 'suggestions'}`);
  }

  setActive(index) {
    this.active = index;
    this.results.querySelectorAll('[role="option"]').forEach((option, i) => {
      const on = i === index;
      option.setAttribute('aria-selected', String(on));
      option.classList.toggle('is-active', on);
    });
    this.input.setAttribute('aria-activedescendant', index >= 0 ? `iw-finder-option-${index}` : '');
  }

  say(message) {
    if (this.status) this.status.textContent = message;
  }

  close() {
    this.results.innerHTML = '';
    this.results.hidden = true;
    this.matches = [];
    this.active = -1;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.say('');
  }

  clear() {
    this.input.value = '';
    this.close();
    if (this.clearButton) this.clearButton.hidden = true;
    // Clearing while a course filter is applied should return the full grid.
    if (this.collectionUrl && window.location.pathname !== this.collectionUrl) {
      window.location.href = this.collectionUrl;
      return;
    }
    this.input.focus();
  }
}

if (!customElements.get('ironwood-course-finder')) {
  customElements.define('ironwood-course-finder', IronwoodCourseFinder);
}
